import { doc, getDoc, setDoc, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../firebase/config';
import { DefaultSet, AssignmentCategory, AssignmentSettings, CoinSettings } from '../types/alarm';
import { firebaseReadCounter } from './firebaseReadCounter';

const COLLECTION_PATH = 'setCollection';
const DEFAULT_SET_DOC_ID = 'DefaultSet';

/**
 * DefaultSet 서비스
 * users/{userId}/classes/{classId}/setCollection/DefaultSet 문서를 관리
 */
export const defaultSetService = {
  /**
   * DefaultSet 문서 경로 생성
   */
  getDocRef(userId: string, classId: string) {
    return doc(db, 'users', userId, 'classes', classId, COLLECTION_PATH, DEFAULT_SET_DOC_ID);
  },

  /**
   * DefaultSet 전체 로드
   */
  async loadDefaultSet(userId: string, classId: string): Promise<DefaultSet | null> {
    try {
      const docRef = this.getDocRef(userId, classId);
      const docSnap = await getDoc(docRef);
      firebaseReadCounter.increment(1);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const result = {
          id: DEFAULT_SET_DOC_ID,
          assignmentCategories: data.assignmentCategories || [],
          assignmentSettings: data.assignmentSettings,
          coinSettings: data.coinSettings,
          profileCardCost: data.profileCardCost ?? 100, // 기본값 100 (하위 호환)
          profileCardCostB: data.profileCardCostB ?? data.profileCardCost ?? 100, // 세트 B
          profileCardCostC: data.profileCardCostC ?? data.profileCardCost ?? 100, // 세트 C
          userCardImageOrder: data.userCardImageOrder || [],
          routineSectionEnabled: data.routineSectionEnabled !== undefined ? data.routineSectionEnabled : true, // 기본값 true
          storeIndividualPurchaseEnabled: data.storeIndividualPurchaseEnabled !== undefined ? data.storeIndividualPurchaseEnabled : true, // 기본값 true
          individualRecordViewLocked: data.individualRecordViewLocked === true,
          individualRecordViewLockPasswordHash: typeof data.individualRecordViewLockPasswordHash === 'string' ? data.individualRecordViewLockPasswordHash : undefined,
          memberProfileDisplayMode: data.memberProfileDisplayMode === 'photo' ? 'photo' : 'svg',
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || undefined,
        } as DefaultSet;
        
        console.log('📖 DefaultSet 로드:', {
          userId,
          classId,
          routineSectionEnabled: {
            raw: data.routineSectionEnabled,
            processed: result.routineSectionEnabled,
            type: typeof data.routineSectionEnabled
          },
          storeIndividualPurchaseEnabled: {
            raw: data.storeIndividualPurchaseEnabled,
            processed: result.storeIndividualPurchaseEnabled,
            type: typeof data.storeIndividualPurchaseEnabled
          }
        });
        
        return result;
      }
      return null;
    } catch (error) {
      console.error('❌ DefaultSet 로드 실패:', error);
      return null;
    }
  },

  /**
   * DefaultSet 초기화 (없으면 생성)
   */
  async initializeDefaultSet(userId: string, classId: string): Promise<DefaultSet> {
    const existing = await this.loadDefaultSet(userId, classId);
    if (existing) {
      return existing;
    }

    const defaultSet: DefaultSet = {
      id: DEFAULT_SET_DOC_ID,
      assignmentCategories: [],
      assignmentSettings: {
        defaultPoints: 10,
        statusDefaultPoints: {
          pending: 0,
          confirmed: 10,
          delayed: 0,
          exempted: 0,
        },
        updatedAt: new Date(),
      },
      coinSettings: {
        defaultPoints: 10,
      },
      createdAt: new Date(),
    };

    await setDoc(this.getDocRef(userId, classId), defaultSet);
    console.log('✅ DefaultSet 초기화 완료:', { userId, classId });
    return defaultSet;
  },

  // ==================== 카테고리 관련 ====================

  /**
   * 과제 카테고리 목록 로드
   */
  async loadCategories(userId: string, classId: string): Promise<AssignmentCategory[]> {
    const defaultSet = await this.loadDefaultSet(userId, classId);
    if (!defaultSet) {
      return [];
    }

    // 기존 카테고리에 색상이 없는 경우 기본 색상 할당
    const defaultColors = [
      '#E74C3C', '#E67E22', '#F1C40F', '#7DCEA0', '#27AE60',
      '#1ABC9C', '#3498DB', '#2980B9', '#9B59B6', '#8E44AD'
    ];

    return (defaultSet.assignmentCategories || []).map((cat, index) => ({
      ...cat,
      color: cat.color || defaultColors[index % defaultColors.length]
    }));
  },

  /**
   * 카테고리 저장 (추가 또는 수정)
   */
  async saveCategory(userId: string, classId: string, category: AssignmentCategory): Promise<void> {
    const defaultSet = await this.loadDefaultSet(userId, classId);
    const categories = defaultSet?.assignmentCategories || [];
    
    // 카테고리 이름의 앞뒤 빈칸 자동 제거
    const trimmedCategory = {
      ...category,
      name: category.name.trim()
    };

    const existingIndex = categories.findIndex(c => c.id === category.id);
    if (existingIndex >= 0) {
      categories[existingIndex] = trimmedCategory;
    } else {
      categories.push(trimmedCategory);
    }

    await updateDoc(this.getDocRef(userId, classId), {
      assignmentCategories: categories,
      updatedAt: new Date()
    });
    console.log('✅ 카테고리 저장 완료:', trimmedCategory.name);
  },

  /**
   * 카테고리 삭제
   */
  async deleteCategory(userId: string, classId: string, categoryId: string): Promise<void> {
    const defaultSet = await this.loadDefaultSet(userId, classId);
    const categories = defaultSet?.assignmentCategories || [];
    
    const filteredCategories = categories.filter(c => c.id !== categoryId);

    await updateDoc(this.getDocRef(userId, classId), {
      assignmentCategories: filteredCategories,
      updatedAt: new Date()
    });
    console.log('✅ 카테고리 삭제 완료:', categoryId);
  },

  // ==================== 과제 설정 관련 ====================

  /**
   * 과제 설정 로드
   */
  async loadAssignmentSettings(userId: string, classId: string): Promise<AssignmentSettings | null> {
    const defaultSet = await this.loadDefaultSet(userId, classId);
    return defaultSet?.assignmentSettings || null;
  },

  /**
   * 과제 설정 저장
   */
  async saveAssignmentSettings(userId: string, classId: string, settings: AssignmentSettings): Promise<void> {
    // DefaultSet이 없으면 먼저 초기화
    const existing = await this.loadDefaultSet(userId, classId);
    if (!existing) {
      await this.initializeDefaultSet(userId, classId);
    }

    await updateDoc(this.getDocRef(userId, classId), {
      assignmentSettings: {
        ...settings,
        updatedAt: new Date()
      },
      updatedAt: new Date()
    });
    console.log('✅ 과제 설정 저장 완료:', settings);
  },

  // ==================== 코인 설정 관련 ====================

  /**
   * 코인 설정 로드
   */
  async loadCoinSettings(userId: string, classId: string): Promise<CoinSettings | null> {
    const defaultSet = await this.loadDefaultSet(userId, classId);
    return defaultSet?.coinSettings || null;
  },

  /**
   * 코인 설정 저장
   */
  async saveCoinSettings(userId: string, classId: string, settings: CoinSettings): Promise<void> {
    // DefaultSet이 없으면 먼저 초기화
    const existing = await this.loadDefaultSet(userId, classId);
    if (!existing) {
      await this.initializeDefaultSet(userId, classId);
    }

    await updateDoc(this.getDocRef(userId, classId), {
      coinSettings: settings,
      updatedAt: new Date()
    });
    console.log('✅ 코인 설정 저장 완료:', settings);
  },

  /**
   * 코인 기본 포인트만 업데이트
   */
  async updateCoinDefaultPoints(userId: string, classId: string, defaultPoints: number): Promise<void> {
    // DefaultSet이 없으면 먼저 초기화
    const existing = await this.loadDefaultSet(userId, classId);
    if (!existing) {
      await this.initializeDefaultSet(userId, classId);
    }

    await updateDoc(this.getDocRef(userId, classId), {
      'coinSettings.defaultPoints': defaultPoints,
      updatedAt: new Date()
    });
    console.log('✅ 코인 기본 포인트 업데이트:', defaultPoints);
  },

  // ==================== 일반 업데이트 ====================

  /**
   * DefaultSet 부분 업데이트 (profileCardCost 등)
   */
  async updateDefaultSet(userId: string, classId: string, updates: Partial<DefaultSet>): Promise<void> {
    // DefaultSet이 없으면 먼저 초기화
    const existing = await this.loadDefaultSet(userId, classId);
    if (!existing) {
      await this.initializeDefaultSet(userId, classId);
    }

    await updateDoc(this.getDocRef(userId, classId), {
      ...updates,
      updatedAt: new Date()
    });
    console.log('✅ DefaultSet 업데이트 완료:', updates);
  },

  /**
   * 개별 접근 페이지 설정 업데이트
   */
  async updateIndividualAccessSettings(
    userId: string, 
    classId: string, 
    settings: { routineSectionEnabled?: boolean; storeIndividualPurchaseEnabled?: boolean }
  ): Promise<void> {
    // DefaultSet이 없으면 먼저 초기화
    const existing = await this.loadDefaultSet(userId, classId);
    if (!existing) {
      await this.initializeDefaultSet(userId, classId);
    }

    // 업데이트할 필드만 포함 (undefined인 필드는 제외, false 값도 명시적으로 저장)
    const updates: any = {
      updatedAt: new Date()
    };
    
    // false 값도 명시적으로 저장 (boolean 타입이면 저장)
    if (typeof settings.routineSectionEnabled === 'boolean') {
      updates.routineSectionEnabled = settings.routineSectionEnabled;
    }
    
    if (typeof settings.storeIndividualPurchaseEnabled === 'boolean') {
      updates.storeIndividualPurchaseEnabled = settings.storeIndividualPurchaseEnabled;
    }

    await updateDoc(this.getDocRef(userId, classId), updates);
    console.log('✅ 개별 접근 페이지 설정 업데이트 완료:', settings);
  },

  /**
   * 개별 기록 열람 잠금 설정 업데이트
   */
  async updateIndividualRecordViewLock(
    userId: string,
    classId: string,
    settings: { individualRecordViewLocked: boolean; individualRecordViewLockPasswordHash?: string | null }
  ): Promise<void> {
    const existing = await this.loadDefaultSet(userId, classId);
    if (!existing) {
      await this.initializeDefaultSet(userId, classId);
    }
    const updates: Record<string, unknown> = {
      individualRecordViewLocked: settings.individualRecordViewLocked,
      updatedAt: new Date(),
    };
    if (!settings.individualRecordViewLocked) {
      updates.individualRecordViewLockPasswordHash = deleteField();
    } else if (settings.individualRecordViewLockPasswordHash != null && settings.individualRecordViewLockPasswordHash !== '') {
      updates.individualRecordViewLockPasswordHash = settings.individualRecordViewLockPasswordHash;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await updateDoc(this.getDocRef(userId, classId), updates as any);
    console.log('✅ 개별 기록 열람 잠금 설정 업데이트 완료');
  },

  /**
   * 멤버 프로필 표시 방식 저장
   */
  async updateMemberProfileDisplayMode(
    userId: string,
    classId: string,
    mode: 'photo' | 'svg'
  ): Promise<void> {
    const existing = await this.loadDefaultSet(userId, classId);
    if (!existing) {
      await this.initializeDefaultSet(userId, classId);
    }

    await updateDoc(this.getDocRef(userId, classId), {
      memberProfileDisplayMode: mode,
      updatedAt: new Date()
    });
  },

  /**
   * 멤버 프로필 표시 방식 로드
   */
  async loadMemberProfileDisplayMode(
    userId: string,
    classId: string
  ): Promise<'photo' | 'svg'> {
    const defaultSet = await this.loadDefaultSet(userId, classId);
    if (defaultSet?.memberProfileDisplayMode === 'photo') return 'photo';
    return 'svg';
  },
};

